import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Pencil, List, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { EMPTY_NAV_LINKS, PLH_LABEL, PLH_HREF } from "../../lib/constants";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { adminFetch, updateNavigation } from "../../lib/api";
import { logger } from "../../lib/logger";

export default function AdminListsEditor({ readOnly = false, navigation, setNavigation, saving, handleSaveNavigation, activeLang }) {
  const items = navigation || [];
  const initialRef = useRef(null);
  if (!initialRef.current) initialRef.current = JSON.parse(JSON.stringify(items));
  const [confirmIdx, setConfirmIdx] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ label: "", href: "" });
  const [savingNav, setSavingNav] = useState(false);

  useEffect(() => {
    if (confirmIdx === null && !modal) return;
    function onKey(e) {
      if (e.key === "Escape") { setConfirmIdx(null); setModal(null); setForm({ label: "", href: "" }); }
      if (e.key === "Enter" && modal) { saveModal(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmIdx, modal, form]);

  async function persist(newItems) {
    setNavigation(newItems);
    setSavingNav(true);
    try {
      const initial = initialRef.current;
      // Edits: compare current vs initial by uid
      for (const item of newItems) {
        if (item.uid) {
          const orig = initial.find(i => i.uid === item.uid);
          const origIdx = initial.findIndex(i => i.uid === item.uid);
          const newIdx = newItems.indexOf(item);
          const labelChanged = orig && orig.label !== item.label;
          const hrefChanged = orig && orig.href !== item.href;
          const orderChanged = origIdx !== -1 && origIdx !== newIdx;
          if (labelChanged || hrefChanged || orderChanged) {
            const body = { uid: item.uid };
            if (labelChanged) body.label = item.label;
            if (hrefChanged) body.href = item.href;
            if (orderChanged) body.order_index = newIdx;
            await adminFetch("navigation", { method: "PATCH", body });
          }
        }
      }
      // New items
      for (const item of newItems) {
        if (!item.uid) {
          const idx = newItems.indexOf(item);
          const created = await adminFetch("navigation", { method: "POST", body: { locale: activeLang, label: item.label, href: item.href, order_index: idx, active: 1 } });
          if (created?.uid) item.uid = created.uid;
        }
      }
      // Deletions
      const currentUids = newItems.filter(i => i.uid).map(i => i.uid);
      for (const orig of initial) {
        if (orig.uid && !currentUids.includes(orig.uid)) {
          await adminFetch("navigation", { method: "PATCH", body: { uid: orig.uid, active: 0, deleted_at: new Date().toISOString() } });
        }
      }
      initialRef.current = JSON.parse(JSON.stringify(newItems));
      toast.success(`Menu de navegação atualizado (${activeLang}).`);
    } catch (e) { logger.error(e); toast.error("Erro ao salvar navegação."); }
    setSavingNav(false);
  }

  function openNew() { setForm({ label: "", href: "#" }); setModal("new"); }
  function openEdit(idx) { setForm({ label: items[idx].label, href: items[idx].href }); setModal(idx); }
  function closeModal() { setModal(null); setForm({ label: "", href: "" }); }

  async function saveModal() {
    if (!form.label.trim()) return;
    const newItem = modal === "new" ? { label: form.label.trim(), href: form.href.trim() || "#" } : { ...items[modal], label: form.label.trim(), href: form.href.trim() || "#" };
    const newItems = modal === "new" ? [...items, newItem] : items.map((it, i) => i === modal ? newItem : it);
    await persist(newItems);
    closeModal();
  }

  async function move(idx, dir) {
    const to = idx + dir;
    if (to < 0 || to >= items.length) return;
    const n = [...items];
    [n[idx], n[to]] = [n[to], n[idx]];
    await persist(n);
  }

  return (
    <>
      {/* Delete confirmation */}
      <AlertDialog open={confirmIdx !== null} onOpenChange={() => setConfirmIdx(null)}>
        <AlertDialogContent className="max-w-md border border-fendi/10 bg-offwhite">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-softblack">Eliminar link</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Tem a certeza que deseja eliminar <strong>"{confirmIdx !== null ? items[confirmIdx]?.label : ""}"</strong> do menu?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-8 gap-3">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-2.5 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { await persist(items.filter((_, i) => i !== confirmIdx)); setConfirmIdx(null); }}
              className="bg-destructive text-white hover:bg-destructive/90 !py-2.5 !px-8 text-[11px] uppercase tracking-widest rounded-none">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add / Edit modal */}
      <AlertDialog open={modal !== null} onOpenChange={(v) => { if (!v) closeModal(); }}>
        <AlertDialogContent className="max-w-md border border-fendi/10 bg-offwhite" onClick={e => e.stopPropagation()}>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0">
                {modal === "new" ? <Plus size={18} /> : <Pencil size={18} />}
              </div>
              <div>
                <AlertDialogTitle className="font-serif text-xl text-softblack">{modal === "new" ? "Novo Link" : "Editar Link"}</AlertDialogTitle>
                <AlertDialogDescription className="text-warmgray text-xs font-light mt-0.5">
                  {modal === "new" ? "Adicione um novo item ao menu de navegação." : "Altere o label ou o destino do link."}
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <span className="text-[9px] uppercase tracking-widest text-warmgray font-medium">Label <span className="text-red-400">*</span></span>
              <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder={PLH_LABEL}
                className="w-full mt-1.5 px-4 py-2.5 text-sm border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" autoFocus />
            </div>
            <div>
              <span className="text-[9px] uppercase tracking-widest text-warmgray font-medium">Link</span>
              <input value={form.href} onChange={e => setForm({ ...form, href: e.target.value })}
                placeholder={PLH_HREF}
                className="w-full mt-1.5 px-4 py-2.5 text-sm border border-fendi/30 rounded-lg bg-transparent font-mono focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
            </div>
          </div>
          <AlertDialogFooter className="mt-4 gap-3">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-2.5 !px-8 text-[10px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={saveModal} disabled={!form.label.trim()}
              className="bg-softblack text-white hover:bg-softblack/90 !py-2.5 !px-8 text-[10px] uppercase tracking-widest rounded-none disabled:opacity-50">
              {modal === "new" ? "Adicionar" : "Guardar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div data-id="admin-lists" className="w-full animate-in fade-in slide-in-from-bottom-2 duration-500">
        <div className="bg-white border border-fendi/40 rounded-xl shadow-sm">
          {/* Header */}
          <div className="flex items-center justify-between px-3 md:px-5 py-3 border-b border-fendi/10 bg-fendi/5">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0">
                <List size={13} className="md:hidden" /><List size={15} className="hidden md:inline" />
              </div>
              <div className="min-w-0">
                <h3 className="text-xs md:text-sm font-medium text-softblack truncate">Links do Menu</h3>
                <p className="text-[9px] md:text-[10px] text-warmgray truncate">{items.length} {items.length === 1 ? "link" : "links"}</p>
              </div>
            </div>
            {!readOnly && (
              <button onClick={openNew}
                className="flex items-center gap-1 px-2.5 md:px-3 py-1.5 bg-champagne/10 text-champagne hover:bg-champagne/20 rounded-md text-[9px] md:text-[10px] uppercase tracking-widest font-medium transition-all shrink-0">
                <Plus size={12} /> Novo
              </button>
            )}
          </div>

          {/* Items */}
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 md:py-14 px-4 text-center">
              <div className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-fendi/5 flex items-center justify-center mb-3">
                <List size={16} className="md:hidden text-fendi/30" /><List size={20} className="hidden md:inline text-fendi/30" />
              </div>
              <p className="text-xs md:text-sm text-warmgray font-medium">{EMPTY_NAV_LINKS}</p>
              {!readOnly && (
                <button onClick={openNew} className="mt-3 inline-flex items-center gap-1 text-[9px] md:text-[10px] uppercase tracking-widest text-champagne hover:text-softblack transition-colors font-medium">
                  <Plus size={12} /> Adicionar primeiro link
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-fendi/10">
              {/* Desktop table */}
              <table className="hidden md:table w-full text-xs">
                <thead>
                  <tr className="border-b border-fendi/10 bg-fendi/[0.03]">
                    <th className="px-4 py-2.5 text-left text-[9px] uppercase tracking-widest text-warmgray/50 font-medium w-14">#</th>
                    <th className="px-4 py-2.5 text-left text-[9px] uppercase tracking-widest text-warmgray/50 font-medium">Label</th>
                    <th className="px-4 py-2.5 text-left text-[9px] uppercase tracking-widest text-warmgray/50 font-medium">Link</th>
                    <th className="px-4 py-2.5 text-right text-[9px] uppercase tracking-widest text-warmgray/50 font-medium w-24">Acção</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-fendi/10">
                  {items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-fendi/5 transition-colors group">
                      <td className="px-4 py-2.5 align-middle">
                        <div className="flex items-center gap-1.5">
                          <div className="flex flex-col gap-0.5 shrink-0">
                            <button onClick={() => move(idx, -1)} disabled={idx === 0}
                              className="p-0.5 text-warmgray/30 hover:text-softblack disabled:opacity-15"><svg width="6" height="6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 15l-6-6-6 6"/></svg></button>
                            <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1}
                              className="p-0.5 text-warmgray/30 hover:text-softblack disabled:opacity-15"><svg width="6" height="6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6"/></svg></button>
                          </div>
                          <span className="w-4 h-4 rounded-full bg-fendi/10 text-[8px] font-mono text-warmgray/50 flex items-center justify-center">{idx + 1}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 align-middle">
                        <span className="text-sm font-medium text-softblack">{item.label || <span className="italic text-warmgray/40">vazio</span>}</span>
                      </td>
                      <td className="px-4 py-2.5 align-middle">
                        <span className="text-[11px] font-mono text-warmgray/60 flex items-center gap-1.5">
                          <ExternalLink size={10} className="shrink-0" />
                          <span className="truncate max-w-[150px] inline-block">{item.href || "#"}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2.5 align-middle text-right">
                        {!readOnly && (
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => openEdit(idx)} className="p-1.5 rounded-md text-warmgray/40 hover:text-champagne hover:bg-champagne/5 transition-all" title="Editar"><Pencil size={13} /></button>
                            <button onClick={() => setConfirmIdx(idx)} className="p-1.5 rounded-md text-warmgray/40 hover:text-destructive hover:bg-red-50 transition-all" title="Eliminar"><Trash2 size={13} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile cards */}
              <div className="md:hidden space-y-1 p-2">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2.5 bg-white rounded-lg border border-fendi/20 hover:border-fendi/40 transition-colors">
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0}
                        className="p-0.5 text-warmgray/30 hover:text-softblack disabled:opacity-15"><svg width="5" height="5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 15l-6-6-6 6"/></svg></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1}
                        className="p-0.5 text-warmgray/30 hover:text-softblack disabled:opacity-15"><svg width="5" height="5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6"/></svg></button>
                    </div>
                    <span className="w-4 h-4 rounded-full bg-fendi/10 text-[8px] font-mono text-warmgray/50 flex items-center justify-center shrink-0">{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-softblack truncate">{item.label || <span className="italic text-warmgray/40">vazio</span>}</p>
                      <p className="text-[10px] font-mono text-warmgray/60 truncate flex items-center gap-1 mt-0.5">
                        <ExternalLink size={9} className="shrink-0" />
                        {item.href || "#"}
                      </p>
                    </div>
                    {!readOnly && (
                      <div className="flex gap-0.5 shrink-0">
                        <button onClick={() => openEdit(idx)} className="p-1.5 rounded-md text-warmgray/40 hover:text-champagne hover:bg-champagne/5 transition-all" title="Editar"><Pencil size={12} /></button>
                        <button onClick={() => setConfirmIdx(idx)} className="p-1.5 rounded-md text-warmgray/40 hover:text-destructive hover:bg-red-50 transition-all" title="Eliminar"><Trash2 size={12} /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
